import { useNavigate } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { JsonValue } from '@components/json-editor';
import { useToaster } from '@components/toaster';
import { minutes } from '@utils/milliseconds';

import { api } from 'api';

import { updateUserOptions } from 'api/authentication';
import { useStore } from 'models/Provider';
import { Feature, IWhoAmI, UserOptions } from 'types/authentication';

export enum AuthenticationQueryKey {
  CheckSession = 'check-session',
  WhoAmI = 'who-am-i',
}

export function useSessionCheck() {
  return useQuery({
    queryKey: [AuthenticationQueryKey.CheckSession],
    queryFn: api.authentication.checkSession,
    staleTime: Infinity,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useWhoAmI(options?: UseQueryOptions<IWhoAmI, AxiosError>) {
  return useQuery({
    queryKey: [AuthenticationQueryKey.WhoAmI],
    queryFn: api.authentication.whoAmI,
    staleTime: Infinity,
    ...options,
  });
}

const useInvalidateWhoAmI = () => {
  const queryClient = useQueryClient();

  const invalidateWhoAmI = () => queryClient.invalidateQueries([AuthenticationQueryKey.WhoAmI]);
  return { invalidateWhoAmI };
};

export const useCurrentUser = () => {
  const query = useWhoAmI({
    retryOnMount: false,
    refetchInterval: minutes(10),
    refetchOnWindowFocus: 'always',
  });

  return query.data;
};

export const useUserOption = <K extends keyof UserOptions>(key: K, defaultValue: JsonValue) => {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const { invalidateWhoAmI } = useInvalidateWhoAmI();
  const update = useMutation(
    (value: UserOptions[K]) => {
      queryClient.setQueryData([AuthenticationQueryKey.WhoAmI], (current: IWhoAmI | undefined) => {
        if (current?.options) {
          current.options[key] = value;
          return { ...current };
        }
      });
      return updateUserOptions({ key, value });
    },
    {
      onSuccess: invalidateWhoAmI,
    }
  );
  return { update, value: currentUser?.options?.[key] ?? defaultValue };
};

export function useSignOut() {
  const client = useQueryClient();
  return useMutation(api.authentication.signOut, {
    async onSuccess() {
      api.global.removeDebugToken();
      await client.resetQueries();
    },
  });
}

export function useSwitchCompany(toPath = '/') {
  const client = useQueryClient();
  const { t } = useStore();
  const { showSuccess } = useToaster();
  const navigate = useNavigate();
  return useMutation(api.authentication.switchCompany, {
    async onSuccess(_, variables) {
      api.global.removeDebugToken();
      navigate(toPath, { replace: true });
      await client.resetQueries();
      showSuccess(t('companies.successfulSwitch', variables.displayName));
    },
  });
}

export const useFeature = (feature: Feature) => {
  const { data: currentUser } = useWhoAmI();
  return currentUser?.company.features?.includes(feature) ?? false;
};
